import { createUrl } from "@/redux/api";
import { convertToFormData } from "@/shared/utils/forms";
import {
  ProfileTypeQueryParams,
  UserProfileTypeQueryParams,
  UserQueryParams,
  UserTags,
  UsersTagTypes,
  FullUserResponse,
  UserProfileTypeResponse,
  ProfileTypeResponse,
  BaseUserResponse,
  UpdateUserRequest,
  DeleteUserRequest,
  UserExportDownload,
  StartUserExportResponse,
  UserProfileTypeRequest,
  ExpertiseAreaQueryParams,
  ExpertiseAreaResponse,
  ImpersonateResponse,
  PasswordResetLinkResponse,
  CookieConsentResponse,
  UpdateCookieConsentRequest,
} from "./types";
import { isPaginatedResponse } from "@/redux/typeGuards";
import { BuilderType, PaginatedResponse } from "@/redux/types";

const usersService = (builder: BuilderType<UserTags>) => ({
  getUsers: builder.query<
    BaseUserResponse[] | PaginatedResponse<BaseUserResponse>,
    UserQueryParams
  >({
    query: (filters) => createUrl("/users/", filters),
    providesTags: (result): UsersTagTypes[] =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as BaseUserResponse[]
            ).map(
              (i): UsersTagTypes => ({
                type: "User",
                id: i.id,
              }),
            ),
            { type: "Users" },
          ]
        : [],
  }),
  getUserById: builder.query<FullUserResponse, string | number>({
    query: (id) => `/users/${id}/`,
    providesTags: (result) =>
      result ? [{ type: "User", id: result.id }, { type: "Users" }] : [],
  }),
  updateUserById: builder.mutation<
    FullUserResponse,
    { id: number; data: UpdateUserRequest }
  >({
    query: ({ id, data }) => ({
      url: `/users/${id}/`,
      method: "PUT",
      data: convertToFormData(data),
    }),
    invalidatesTags: (result): UsersTagTypes[] =>
      result ? [{ type: "User", id: result.id }, { type: "Users" }] : [],
  }),
  patchUserById: builder.mutation<
    FullUserResponse,
    { id: number; data: UpdateUserRequest }
  >({
    query: ({ id, data }) => ({
      url: `/users/${id}/`,
      method: "PATCH",
      data: convertToFormData(data),
    }),
    invalidatesTags: (result): UsersTagTypes[] =>
      result ? [{ type: "User", id: result.id }, { type: "Users" }] : [],
  }),
  deleteUserById: builder.mutation<
    null,
    { id: number; data: DeleteUserRequest }
  >({
    query: ({ id, data }) => ({
      url: `/users/${id}/`,
      method: "DELETE",
      data,
    }),
    invalidatesTags: (_, error, args): UsersTagTypes[] =>
      error ? [] : [{ type: "User", id: args.id }],
  }),
  impersonateUser: builder.query<ImpersonateResponse, number | string>({
    query: (id) => ({
      url: `/users/${id}/impersonate/`,
      method: "GET",
    }),
  }),
  getPasswordResetLink: builder.query<
    PasswordResetLinkResponse,
    number | string
  >({
    query: (id) => ({
      url: `/users/${id}/password-reset-link/`,
      method: "GET",
    }),
  }),
  getCookieConsent: builder.query<CookieConsentResponse, void>({
    query: () => ({
      url: `/users/me/cookie-consent/`,
      method: "GET",
    }),
    providesTags: (): UsersTagTypes[] => [{ type: "CookieConsent" }],
  }),
  updateCookieConsent: builder.mutation<
    CookieConsentResponse,
    UpdateCookieConsentRequest
  >({
    query: (data) => ({
      url: `/users/me/cookie-consent/`,
      method: "PUT",
      data,
    }),
    invalidatesTags: (): UsersTagTypes[] => [{ type: "CookieConsent" }],
  }),
  exportUserData: builder.mutation<StartUserExportResponse, number>({
    query: (id) => ({
      url: `/users/${id}/export/`,
      method: "POST",
    }),
  }),
  getExportedUserData: builder.query<UserExportDownload, number>({
    query: (id) => ({
      url: `/users/${id}/export/`,
      method: "GET",
    }),
  }),
  getProfileTypes: builder.query<ProfileTypeResponse[], ProfileTypeQueryParams>(
    {
      query: (params) => createUrl("/profile-types/", params),
      providesTags: (result): UsersTagTypes[] =>
        result
          ? [
              ...(
                (isPaginatedResponse(result)
                  ? result.results
                  : result) as ProfileTypeResponse[]
              ).map(
                (type): UsersTagTypes => ({
                  type: "ProfileType",
                  id: type.id,
                }),
              ),
              { type: "ProfileTypes" },
            ]
          : [],
    },
  ),
  getUserProfileTypes: builder.query<
    UserProfileTypeResponse[],
    UserProfileTypeQueryParams
  >({
    query: (params) => ({
      url: createUrl("/user-profile-types/", params),
      method: "GET",
    }),
    providesTags: (result): UsersTagTypes[] =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as UserProfileTypeResponse[]
            ).map(
              (type): UsersTagTypes => ({
                type: "UserProfileType",
                id: type.id,
              }),
            ),
            { type: "UserProfileTypes" },
          ]
        : [],
  }),
  getUserProfileTypeById: builder.query<
    UserProfileTypeResponse,
    number | string
  >({
    query: (id) => ({
      url: `/user-profile-types/${id}/`,
      method: "GET",
    }),
    providesTags: (result) =>
      result
        ? [
            { type: "UserProfileType", id: result.id },
            { type: "UserProfileTypes" },
          ]
        : [],
  }),
  createUserProfileType: builder.mutation<
    UserProfileTypeResponse,
    UserProfileTypeRequest
  >({
    query: (data) => ({
      url: "/user-profile-types/",
      method: "POST",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            {
              type: "UserProfileTypes",
            },
          ]
        : [],
  }),
  updateUserProfileType: builder.mutation<
    UserProfileTypeResponse,
    { id: number; data: UserProfileTypeRequest }
  >({
    query: ({ id, data }) => ({
      url: `/user-profile-types/${id}/`,
      method: "PUT",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            {
              type: "UserProfileType",
              id: result.id,
            },
          ]
        : [],
  }),
  patchUserProfileType: builder.mutation<
    UserProfileTypeResponse,
    { id: number; data: Partial<UserProfileTypeRequest> }
  >({
    query: ({ id, data }) => ({
      url: `/user-profile-types/${id}/`,
      method: "PATCH",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            {
              type: "UserProfileType",
              id: result.id,
            },
          ]
        : [],
  }),
  getAreasOfExpertise: builder.query<
    ExpertiseAreaResponse[] | PaginatedResponse<ExpertiseAreaResponse>,
    ExpertiseAreaQueryParams
  >({
    query: (filters) => ({
      url: createUrl("/expertise-areas/", filters),
      method: "GET",
    }),
    providesTags: (result): UsersTagTypes[] =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as ExpertiseAreaResponse[]
            ).map(
              (i): UsersTagTypes => ({
                type: "ExpertiseArea",
                id: i.id,
              }),
            ),
            { type: "ExpertiseAreas" },
          ]
        : [],
  }),
});

export default usersService;
